import { differenceInDays } from 'date-fns';

import { expVal } from '@confluence/feature-experiments';
import { useIsNewUser } from '@confluence/targeting/entry-points/useIsNewUser.ts';
import { useSiteTraits } from '@confluence/targeting/entry-points/useSiteTraits.ts';

const FIRST_THIRTY_DAYS_THRESHOLD = 30;
const FIRST_ACTIVATION_DATE_TRAIT = 'firstActivationDate';
const AUTO_PROVISIONED_TRAIT = 'is_jira_confluence_d0_auto-provisioned';

/**
 * Hook to check enrollment in the twcg-2304-import-in-onboarding experiment.
 *
 * Eligibility criteria:
 * - User may be a founding admin or an nth user
 * - User must be within their first 30 days:
 *   - For auto-provisioned (AP) Xflow tenants: uses user-level first active date
 *   - For Land / non-AP Xflow tenants: uses tenant-level first activation date
 *
 * When all eligibility checks pass, fires a Statsig exposure event via expVal.
 *
 * @param options.skip - When true, returns early without firing exposure (useful for deferred rendering)
 * @returns {{ isEnabled: boolean, loading: boolean }}
 */
export const useTwcg2304ImportInOnboarding = ({ skip }: { skip?: boolean } = {}): {
	isEnabled: boolean;
	loading: boolean;
} => {
	// User-level: check if this user's first active date is within 30 days (for AP Xflow)
	const { isNewUser, loading: isNewUserLoading } = useIsNewUser(
		{ activeDaysThreshold: FIRST_THIRTY_DAYS_THRESHOLD },
		Boolean(skip),
	);

	// Tenant-level: check site traits for activation date and auto-provisioned status
	const { data: siteTraitsData, loading: isSiteTraitsLoading } = useSiteTraits();
	const siteActivationDate = siteTraitsData?.attributes?.find(
		({ name }) => name === FIRST_ACTIVATION_DATE_TRAIT,
	)?.value;
	const isSiteNewlyActivated =
		Boolean(siteActivationDate) &&
		differenceInDays(new Date(), new Date(siteActivationDate as string)) <
			FIRST_THIRTY_DAYS_THRESHOLD;

	const isAutoProvisioned =
		siteTraitsData?.attributes?.find(({ name }) => name === AUTO_PROVISIONED_TRAIT)?.value ===
		'true';

	// When skip is true, return without calling expVal (which fires exposure).
	// This allows consumers to defer the exposure event until the UI is actually rendered.
	// Note: hooks above must still be called unconditionally to satisfy React's rules of hooks.
	if (skip) {
		return { isEnabled: false, loading: false };
	}

	// If still loading, don't enable yet
	if (isNewUserLoading || isSiteTraitsLoading) {
		return { isEnabled: false, loading: true };
	}

	// Determine if the tenant/user is within the first 30 days based on tenant type:
	// - AP Xflow: use user-level date (confluence_first_active via useIsNewUser)
	// - Non-AP Xflow / Land: use tenant-level date (firstActivationDate)
	const isWithinFirstThirtyDays = isAutoProvisioned ? isNewUser : isSiteNewlyActivated;

	if (!isWithinFirstThirtyDays) {
		return { isEnabled: false, loading: false };
	}

	return {
		isEnabled: expVal('twcg-2304-import-in-onboarding', 'isEnabled', false),
		loading: false,
	};
};
